// components/create/CoHostsSection.js
//
// Co-hosts: the other people who can edit this invitation and see who is
// coming. The app has had this since co-hosting shipped; the website's More
// tab never offered it, so an invitation made here had exactly one host and
// no way to add another without opening the app.
//
// ── Why it only appears after the invitation exists ────────────────────────
//
// `event_cohosts` rows need an `event_id`, and there is no event id until the
// invitation has been saved once. The app has the same constraint and says so
// rather than showing a control that cannot work — so on a brand-new
// invitation this is a sentence, and after the first save it is a picker.

import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiSearch, FiUserPlus } from 'react-icons/fi';
import supabase from '../../services/supabase';
import { useAuth } from '../../services/auth';
import '../../styles/CoHostsSection.scss';
import Icon from '../Icon';

const displayName = (u) =>
  [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.username || 'Someone';

export default function CoHostsSection({ eventId, financialYear }) {
  const { user } = useAuth();

  const [coHosts, setCoHosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('event_cohosts')
        .select('id, status, financial_year, user:users!fk_event_cohosts_user(id, first_name, last_name, username, profile_photo_cdn_url)')
        .eq('event_id', eventId);
      if (err) throw err;
      setCoHosts(data ?? []);
    } catch (err) {
      console.error('Error loading co-hosts:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // Two characters before searching, as the app requires: a one-letter query
  // matches most of the user table and is a slow way to learn nothing.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await supabase
          .from('users')
          .select('id, first_name, last_name, username, profile_photo_cdn_url')
          .or(`username.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
          .limit(15);
        if (!cancelled) setResults(data ?? []);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const add = async (person) => {
    setError(null);

    if (person.id === user?.id) {
      setError('You are already the host.');
      return;
    }
    if (coHosts.some((c) => c.user?.id === person.id)) {
      setError(`${displayName(person)} is already a co-host.`);
      return;
    }

    try {
      const { error: err } = await supabase
        .from('event_cohosts')
        .insert({
          event_id: eventId,
          financial_year: financialYear,
          user_id: person.id,
          invited_by: user.id,
          // Pending until they accept. A co-host who has not agreed should
          // not silently gain edit rights over someone else's invitation.
          status: 'pending',
          created_at: new Date().toISOString(),
        });
      if (err) throw err;

      setQuery('');
      setResults([]);
      setShowSearch(false);
      await load();
    } catch (err) {
      setError(err.message || 'Could not add that person.');
    }
  };

  const remove = async (coHost) => {
    if (!window.confirm(`Remove ${displayName(coHost.user)} as a co-host?`)) return;
    try {
      const { error: err } = await supabase
        .from('event_cohosts')
        .delete()
        .eq('id', coHost.id)
        .eq('financial_year', coHost.financial_year);
      if (err) throw err;
      setCoHosts((prev) => prev.filter((c) => c.id !== coHost.id));
    } catch (err) {
      setError(err.message || 'Could not remove that co-host.');
    }
  };

  return (
    <div className="section-card">
      <label className="section-title">Co-hosts</label>
      <p className="section-description">
        People who can edit this invitation and see who is coming.
      </p>

      {!eventId ? (
        <p className="cohost-note">
          You can add co-hosts once this invitation is saved — they are attached to
          the invitation, and it does not exist yet.
        </p>
      ) : (
        <>
          {error && <p className="cohost-error">{error}</p>}

          {loading ? (
            <p className="cohost-note">Loading…</p>
          ) : coHosts.length === 0 ? (
            <p className="cohost-note">No co-hosts yet.</p>
          ) : (
            <ul className="cohost-list">
              {coHosts.map((coHost) => (
                <li key={coHost.id}>
                  <span className="cohost-avatar">
                    {coHost.user?.profile_photo_cdn_url ? (
                      <img src={coHost.user.profile_photo_cdn_url} alt="" />
                    ) : (
                      displayName(coHost.user).charAt(0).toUpperCase()
                    )}
                  </span>
                  <span className="cohost-text">
                    <strong>{displayName(coHost.user)}</strong>
                    <small>
                      {coHost.user?.username ? `@${coHost.user.username} · ` : ''}
                      {coHost.status === 'pending' ? 'Invited' : 'Accepted'}
                    </small>
                  </span>
                  <button
                    type="button"
                    className="cohost-remove"
                    onClick={() => remove(coHost)}
                    aria-label={`Remove ${displayName(coHost.user)}`}
                  >
                    <FiX />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {showSearch ? (
            <div className="cohost-search">
              <div className="cohost-search-field">
                <FiSearch />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setError(null); }}
                  placeholder="Search by name or username"
                  aria-label="Search people"
                  autoFocus
                />
                <button type="button" onClick={() => { setShowSearch(false); setQuery(''); }} aria-label="Cancel">
                  <FiX />
                </button>
              </div>

              {query.trim().length < 2 ? (
                <p className="cohost-note">Type at least two characters.</p>
              ) : searching ? (
                <p className="cohost-note">Searching…</p>
              ) : results.length === 0 ? (
                <p className="cohost-note">Nobody found.</p>
              ) : (
                <ul className="cohost-results">
                  {results.map((person) => (
                    <li key={person.id}>
                      <button type="button" onClick={() => add(person)}>
                        <span className="cohost-avatar">
                          {person.profile_photo_cdn_url ? (
                            <img src={person.profile_photo_cdn_url} alt="" />
                          ) : (
                            displayName(person).charAt(0).toUpperCase()
                          )}
                        </span>
                        <span className="cohost-text">
                          <strong>{displayName(person)}</strong>
                          {person.username && <small>@{person.username}</small>}
                        </span>
                        <Icon name="add-circle" size={20} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <button type="button" className="add-button" onClick={() => setShowSearch(true)}>
              <FiUserPlus />
              <span>Add co-host</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
